import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, Heart, LayoutGrid, List, Lock, MapPin, Users, X } from "lucide-react";
import { api, ApiError, assetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import Loader from "@/components/ui/loader";
import { PageHero } from "@/components/interior-page";
import { Pagination, usePageSize } from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { useToast } from "@/components/ui/toast";
import ListingHotBadge from "@/components/ListingHotBadge";

// Multiples of 12 fill every row of the 2/3/4-up card grid.
const PAGE_SIZE_OPTIONS = [12, 24, 48];
const DEFAULT_PAGE_SIZE = 24;

interface LookupItem { id: number; name: string }

interface FavoriteItem {
  id: string;
  listing_id: string;
  created_at: string;
  listing_created_at: string | null;
  listing_published_at: string | null;
  listing_title: string | null;
  listing_reference_code: string | null;
  listing_category_id: number | null;
  listing_status_id: number | null;
  listing_status_name: string | null;
  asking_price: number | null;
  revenue: number | null;
  ebitda: number | null;
  country_id: number | null;
  state_id: number | null;
  city_id: number | null;
  city_name: string | null;
  state_name: string | null;
  country_name: string | null;
  agent_name: string | null;
  agent_avatar_url: string | null;
  listing_image_url: string | null;
  display_overview: string | null;
}

const fmtCompact = (v: number | null): string => {
  if (!v) return "—";
  if (v >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(v % 1_000_000_000 === 0 ? 0 : 1)}b`;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}m`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(v % 1_000 === 0 ? 0 : 1)}k`;
  return `$${v}`;
};

const ACTIVE_STATUS_ID = 3;
const STATUS_ACRONYMS = new Set(["loi"]);
// "under_offer" -> "Under Offer", "loi" -> "LOI"
const statusLabel = (name: string) =>
  name.split("_").map((w) => (STATUS_ACRONYMS.has(w) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1))).join(" ");

// A saved deal that has moved past "active" (LOI, under offer, sold...) —
// the main thing a buyer checks their shortlist for.
function StatusBadge({ item, className }: { item: FavoriteItem; className?: string }) {
  if (!item.listing_status_name || item.listing_status_id === ACTIVE_STATUS_ID) return null;
  const sold = item.listing_status_name === "sold";
  return (
    <span className={cn(
      "inline-flex items-center rounded-full border px-2.5 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.1em] whitespace-nowrap",
      sold ? "border-rose-400/40 bg-rose-500/15 text-rose-500" : "border-amber-400/40 bg-amber-500/15 text-amber-600",
      className,
    )}>
      {statusLabel(item.listing_status_name)}
    </span>
  );
}

export default function FavoriteListingsPage() {
  const toast = useToast();
  // Buyers view this page inside the left sidebar shell, so fill the wider column.
  // Full width for every role — see PageShell in components/interior-page.tsx.
  const shellWidth = "w-full";
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [loading, setLoading] = useState(true);
  // Page lives in the URL (?page=N, 1-based) so returning from a listing detail
  // via "Back to listings" restores the exact page the user was on.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next > 1) p.set("page", String(next));
        else p.delete("page");
        return p;
      },
      { replace: true },
    );
  };
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = usePageSize("favorites.pageSize", PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE);
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [categories, setCategories] = useState<LookupItem[]>([]);
  // "subscription" = lapsed/free buyer (API 402); "failed" = anything else.
  // Kept distinct from an empty list so an outage never reads as "no favorites".
  const [loadError, setLoadError] = useState<"subscription" | "failed" | null>(null);

  useEffect(() => {
    api.get<LookupItem[]>("/lookups/categories").then(setCategories).catch(() => {});
  }, []);

  // silent: refresh in place (after a removal) without the full-page loader.
  // size: passed explicitly when the picker changes, since state hasn't updated yet.
  function fetchFavorites(
    targetPage = page,
    { silent = false, size = pageSize }: { silent?: boolean; size?: number } = {},
  ) {
    if (!silent) setLoading(true);
    api.get<{ total_elements: number; data: FavoriteItem[] }>(`/favorites/?page=${targetPage}&page_size=${size}`)
      .then((res) => {
        const lastPage = Math.max(1, Math.ceil(res.total_elements / size));
        // Landed past the end (e.g. a stale ?page= link) — step back to the last real page.
        if (res.data.length === 0 && targetPage > lastPage) {
          fetchFavorites(lastPage, { silent, size });
          return;
        }
        setFavorites(res.data);
        setTotal(res.total_elements);
        setLoadError(null);
        setPage(targetPage);
      })
      .catch((err) => {
        if (silent) return;
        setLoadError(err instanceof ApiError && err.status === 402 ? "subscription" : "failed");
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => { fetchFavorites(page); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Passed as router state on each listing link so ListingDetail's "Back to
  // listings" returns to this exact page. Derived from the current pathname
  // because this component serves several role prefixes (/buyer/favorites,
  // /seller/favorites, /bl-advisor/favorites).
  const { pathname } = useLocation();
  const backTo = page > 1 ? `${pathname}?page=${page}` : pathname;

  const catName = (id: number | null) => id ? (categories.find((c) => c.id === id)?.name ?? "Business") : "Business";
  const locationLabel = (item: FavoriteItem) =>
    [item.city_name, item.state_name, item.country_name].filter(Boolean).join(", ");
  const cfMult = (item: FavoriteItem) =>
    item.asking_price && item.ebitda && item.ebitda !== 0
      ? (item.asking_price / item.ebitda).toFixed(2) + "X"
      : null;

  const handleRemove = async (listingId: string) => {
    setRemovingId(listingId);
    try {
      await api.delete(`/favorites/${listingId}`);
      const wasLastOnPage = favorites.length === 1;
      setFavorites((prev) => prev.filter((f) => f.listing_id !== listingId));
      setTotal((t) => Math.max(0, t - 1));
      toast.success("Removed from favorites");
      // Backfill from the next page, or step back if this page is now empty.
      fetchFavorites(wasLastOnPage && page > 1 ? page - 1 : page, { silent: true });
    } catch {
      toast.error("Failed to remove favorite");
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) return <div className="flex flex-1 items-center justify-center py-24"><Loader /></div>;

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Shared Dark · Gold interior hero — the same header AI Deal Matching
          and the rest of the interior pages use. */}
      <div className={`${shellWidth} px-4 pt-8 sm:px-6 lg:px-8`}>
        <PageHero
          eyebrow="Saved Deals"
          icon={Heart}
          title="My"
          titleAccent="Favorites"
          description={
            loadError
              ? "Your saved listings, in one place."
              : total === 0
              ? "You haven't saved any listings yet."
              : `${total} listing${total === 1 ? "" : "s"} saved`
          }
        />
      </div>

      {/* View controls — their own row now that the hero spans the width. */}
      <div className={`${shellWidth} px-4 sm:px-6 lg:px-8`}>
        <div className="pt-6">
          <div className="flex flex-wrap items-center justify-end gap-4">
            {total > 0 && !loadError && (
              <div className="flex items-center gap-1 rounded-xl border border-border/80 bg-card p-1 self-start sm:self-auto">
                <button
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    viewMode === "grid"
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )}
                >
                  <LayoutGrid className="h-4 w-4" />
                  <span className="hidden sm:inline">Grid</span>
                </button>
                <button
                  onClick={() => setViewMode("table")}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    viewMode === "table"
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                  )}
                >
                  <List className="h-4 w-4" />
                  <span className="hidden sm:inline">Table</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`${shellWidth} px-4 sm:px-6 lg:px-8 py-8`}>
        {loadError === "subscription" ? (
          <div className="rounded-2xl border border-dashed border-border/80 bg-card px-6 py-16 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Lock className="h-7 w-7" />
            </div>
            <h3 className="mt-4 text-xl font-bold text-foreground">Favorites are a member feature</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto leading-6">
              Choose a plan to save listings and get your shortlist back. Anything you saved before is still here.
            </p>
            <Link to="/choose-plan"
              className="mt-5 inline-flex items-center gap-1.5 rounded-xl bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:brightness-[1.06] transition-[filter]">
              Choose a plan
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : loadError === "failed" ? (
          <div className="rounded-2xl border border-dashed border-border/80 bg-card px-6 py-16 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-500/15 text-rose-500">
              <AlertTriangle className="h-7 w-7" />
            </div>
            <h3 className="mt-4 text-xl font-bold text-foreground">Couldn't load your favorites</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto leading-6">
              Something went wrong on our side. Your saved listings are safe.
            </p>
            <Button className="mt-5" onClick={() => fetchFavorites(page)}>Try again</Button>
          </div>
        ) : total === 0 && !loading ? (
          <div className="rounded-2xl border border-dashed border-border/80 bg-card px-6 py-16 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-400">
              <Heart className="h-7 w-7" />
            </div>
            <h3 className="mt-4 text-xl font-bold text-foreground">No saved listings yet</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto leading-6">
              While browsing, tap the <Heart className="inline h-3.5 w-3.5 fill-rose-400 text-rose-400 mx-0.5" /> heart icon on any listing card to save it here for quick access later.
            </p>
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {favorites.map((item) => {
              const loc = locationLabel(item);
              const cf = cfMult(item);
              const imgSrc = item.listing_image_url ? assetUrl(item.listing_image_url) : null;
              const advisorInitials = item.agent_name
                ? item.agent_name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()
                : "";
              // Same card as the marketplace grid (pages/Home.tsx) so a saved
              // deal looks identical here and where it was favorited.
              return (
                <div key={item.id}
                  style={{ borderColor: "rgba(201,161,74,0.4)", borderWidth: "1px" }}
                  className="hover-lift public-listing-card group flex flex-col overflow-hidden rounded-2xl border border-border/70 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">

                  {/* Cover image */}
                  <div className="relative h-44 w-full shrink-0 overflow-hidden">
                    {imgSrc ? (
                      <>
                        <img src={imgSrc} alt={item.listing_title ?? ""} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/25" />
                      </>
                    ) : (
                      <div className="flex h-full w-full items-end bg-[linear-gradient(135deg,hsl(40_20%_14%),hsl(40_16%_9%))]">
                        <div
                          className="pointer-events-none absolute inset-0 opacity-40"
                          style={{ backgroundImage: "repeating-linear-gradient(135deg, hsl(43 30% 60% / 0.08) 0 10px, transparent 10px 20px)" }}
                        />
                        <span className="relative m-3 text-xs text-white/60">img · {catName(item.listing_category_id).toLowerCase()}</span>
                      </div>
                    )}
                    {/* Category + deal-status badges */}
                    <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5">
                      <span className="inline-flex items-center rounded-md border border-primary/25 bg-black/60 px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-[0.12em] text-white/90 backdrop-blur-sm">
                        {catName(item.listing_category_id)}
                      </span>
                      <StatusBadge item={item} className="backdrop-blur-sm" />
                    </div>
                    {/* Remove-from-favorites heart */}
                    <button
                      onClick={() => handleRemove(item.listing_id)}
                      disabled={removingId === item.listing_id}
                      title="Remove from favorites"
                      className="absolute left-3 bottom-3 flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-black/50 backdrop-blur-sm transition-colors hover:bg-black/70 disabled:opacity-50"
                    >
                      <Heart className="h-4 w-4 fill-rose-500 text-rose-500" />
                    </button>
                    <div className="absolute right-3 top-3 flex flex-col items-end gap-1">
                      <ListingHotBadge published_at={item.listing_published_at} size="md" />
                    </div>
                  </div>

                  {/* Body */}
                  <div className="flex flex-1 flex-col gap-3 p-4">
                    <div>
                      {/* Fixed-height ref-code row keeps titles aligned across cards */}
                      <div className="mb-1 flex h-4 items-center">
                        {item.listing_reference_code && (
                          <span className="font-mono text-xs font-bold tracking-wider text-primary">
                            {item.listing_reference_code}
                          </span>
                        )}
                      </div>
                      <div className="min-h-[4.15rem]">
                        <h3 className="line-clamp-2 font-display text-xl font-bold leading-snug text-foreground">
                          {item.listing_title ?? catName(item.listing_category_id)}
                        </h3>
                      </div>
                    </div>

                    {/* Asking Price headline; Revenue / EBITDA / CF Mult beneath.
                        Always all four so cards line up; missing figures show —. */}
                    <div className="overflow-hidden rounded-xl border border-border/60 bg-primary/[0.06]">
                      <div className="px-4 pb-3 pt-3.5">
                        <div className="text-[0.62rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">Asking Price</div>
                        <div className={cn(
                          "mt-1 truncate text-[1.9rem] font-extrabold leading-none tabular-nums",
                          item.asking_price ? "text-primary" : "text-muted-foreground/40",
                        )}>
                          {item.asking_price ? fmtCompact(item.asking_price) : "—"}
                        </div>
                      </div>
                      <div className="grid grid-cols-3 divide-x divide-border/60 border-t border-border/60 bg-card/60">
                        {([
                          ["Revenue", item.revenue ? fmtCompact(item.revenue) : null],
                          ["EBITDA", item.ebitda ? fmtCompact(item.ebitda) : null],
                          ["CF Mult", cf],
                        ] as [string, string | null][]).map(([label, value]) => (
                          <div key={label} className="min-w-0 px-4 py-2.5">
                            <div className="text-[0.58rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">{label}</div>
                            <div className={cn(
                              "mt-1 truncate text-[0.95rem] font-bold tabular-nums",
                              value ? "text-foreground" : "text-muted-foreground/40",
                            )}>
                              {value ?? "—"}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Location + BL Advisor, pinned to the card bottom */}
                    <div className="mt-auto flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      {loc ? (
                        <span className="flex min-w-0 items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5 shrink-0 text-primary/70" />
                          <span className="truncate">{loc}</span>
                        </span>
                      ) : (
                        <span />
                      )}
                      {item.agent_name && (
                        <span className="flex shrink-0 items-center gap-1.5" title={`BL Advisor: ${item.agent_name}`}>
                          {item.agent_avatar_url ? (
                            <img src={assetUrl(item.agent_avatar_url)!} alt={item.agent_name} className="h-6 w-6 rounded-full object-cover" />
                          ) : (
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-[0.58rem] font-bold text-primary">{advisorInitials}</span>
                          )}
                          <span className="truncate text-foreground/80">{item.agent_name}</span>
                        </span>
                      )}
                    </div>

                    <div className="pt-1">
                      <Link to={`/listings/${item.listing_id}`}
                        state={{ from: backTo }}
                        className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-primary/60 bg-gold-gradient py-2.5 text-sm font-semibold text-primary-foreground transition-[filter] hover:brightness-[1.06]">
                        View details
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-2xl border border-border/80 bg-card shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/70 bg-muted/40">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Asking Price</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Revenue</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">EBITDA</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">C.F. Multiple</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">BL Advisor</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {favorites.map((item) => {
                    const loc = locationLabel(item);
                    const cf = cfMult(item);
                    return (
                      <tr key={item.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3.5">
                          <span className="inline-flex items-center rounded-full border border-primary/15 bg-primary/10 px-2 py-0.5 text-[0.64rem] font-semibold uppercase tracking-[0.1em] text-primary whitespace-nowrap">
                            {catName(item.listing_category_id)}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 max-w-[220px]">
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold text-foreground line-clamp-1">
                              {item.listing_title ?? catName(item.listing_category_id)}
                            </span>
                            {item.listing_reference_code && (
                              <span className="shrink-0 font-mono text-[0.65rem] font-normal text-muted-foreground">{item.listing_reference_code}</span>
                            )}
                            <ListingHotBadge published_at={item.listing_published_at} size="sm" />
                            <StatusBadge item={item} className="px-2 py-0.5" />
                          </div>
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span className="font-semibold text-emerald-700">{fmtCompact(item.asking_price)}</span>
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span className="font-semibold text-sky-700">{fmtCompact(item.revenue)}</span>
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span className="font-semibold text-violet-700">{fmtCompact(item.ebitda)}</span>
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          {cf ? <span className="font-bold text-amber-700">{cf}</span> : <span className="text-muted-foreground/50">—</span>}
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          {loc ? (
                            <span className="inline-flex items-center gap-1 text-muted-foreground">
                              <MapPin className="h-3.5 w-3.5 shrink-0" />
                              {loc}
                            </span>
                          ) : <span className="text-muted-foreground/50">—</span>}
                        </td>
                        <td className="px-4 py-3.5">
                          {item.agent_name ? (
                            <div className="flex items-center gap-1.5">
                              {item.agent_avatar_url ? (
                                <img src={assetUrl(item.agent_avatar_url)!} alt={item.agent_name} className="h-6 w-6 rounded-full object-cover" />
                              ) : (
                                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
                                  <Users className="h-3 w-3" />
                                </div>
                              )}
                              <span className="text-xs text-muted-foreground whitespace-nowrap">{item.agent_name}</span>
                            </div>
                          ) : <span className="text-muted-foreground/50">—</span>}
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-2">
                            <Link to={`/listings/${item.listing_id}`} state={{ from: backTo }}>
                              <Button size="sm" variant="outline" className="border-primary/15 bg-white/80 hover:bg-white whitespace-nowrap">
                                View
                                <ArrowRight className="h-3.5 w-3.5" />
                              </Button>
                            </Link>
                            <button
                              onClick={() => handleRemove(item.listing_id)}
                              disabled={removingId === item.listing_id}
                              title="Remove from favorites"
                              className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-200 bg-rose-50 text-rose-500 transition-colors hover:bg-rose-100 disabled:opacity-50"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loadError && <Pagination
          page={page}
          totalElements={total}
          pageSize={pageSize}
          onChange={(p) => fetchFavorites(p)}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageSizeChange={(size) => { setPageSize(size); fetchFavorites(1, { size }); }}
          className="mt-6"
        />}
      </div>
    </div>
  );
}
